import styled from "styled-components";
import useLessonRegister from "features/teacher/hooks/lesson/useLessonRegister";
import RegisterFooter from "shared/components/RegisterFooter";
import Modal from "shared/components/Modal";
import { useLocation, useNavigate } from "react-router-dom";
import InputWithLabel from "shared/components/InputWithLabel";
import { hw, scrollbar } from "features/homework/homeworkUi";

const LessonRegister = () => {
    const id = useLocation().state;
    const navigate = useNavigate();
    const {
        inputLesson, onChange, getTimeList, teachers, onSaveButtonClick,
        isError, setIsError, isSucceed,
        isOpenNullModal, setIsOpenNullModal,
        isOpenDeleteSucceedModal, setIsOpenDeleteSucceedModal, onDeleteClickHandler,
    } = useLessonRegister();

    const renderTimeOptions = () => (
        <>
            <option value="">시간을 선택해 주세요</option>
            {getTimeList().map((time) => (
                <option key={time} value={time}>{time}</option>
            ))}
        </>
    );

    return (
        <Container>
            <Wrapper>
                <Header>
                    <div>
                        <Title>{id ? "학급 수정" : "학급 등록"}</Title>
                        <Desc>{id ? "선택한 학급 정보를 수정한 뒤 저장하세요." : "학급명·담당 교사·요일을 입력해 생성하세요."}</Desc>
                    </div>
                    <Close type="button" onClick={() => navigate("..")}>닫기</Close>
                </Header>
                <Content>
                    <Modal isOpen={isOpenNullModal} title="ERROR" content="필수 입력란을 모두 입력해 주세요" onClose={() => setIsOpenNullModal(false)} />
                    <Modal isOpen={isOpenDeleteSucceedModal} title="SUCCESS" content="학급이 삭제 되었습니다 새로고침을 눌러주세요" onClose={() => { setIsOpenDeleteSucceedModal(false); navigate(-1); }} />
                    <Modal isOpen={isError} title="ERROR" content="학급생성중 오류가 발생하였습니다" onClose={() => setIsError(false)} />
                    <Modal isOpen={isSucceed} title="SUCCESS" content={id ? "수정되었습니다" : "학급이 생성되었습니다"} onClose={() => navigate("..")} />
                    <Card>
                        <CardTitle>기본 정보</CardTitle>
                        <Fields>
                            <InputWithLabel required={true} label="학급명" name="name" value={inputLesson.name} onChange={onChange} />
                            <Field>
                                <Label>담당선생님</Label>
                                <Select value={inputLesson.teacher} onChange={onChange} name="teacher">
                                    <option value="">선생님을 선택해 주세요</option>
                                    {teachers.map((teacher) => (
                                        <option key={teacher.uid} value={teacher.uid}>{teacher.name}</option>
                                    ))}
                                </Select>
                            </Field>
                            <Field $wide>
                                <Label>수업시간</Label>
                                <TimeRow>
                                    <Select value={inputLesson.time_start} onChange={onChange} name="time_start">{renderTimeOptions()}</Select>
                                    <Range>~</Range>
                                    <Select value={inputLesson.time_end} onChange={onChange} name="time_end">{renderTimeOptions()}</Select>
                                </TimeRow>
                            </Field>
                            <Field $wide>
                                <Label>수업요일</Label>
                                <Days>
                                    {["월", "화", "수", "목", "금", "토", "일"].map((day) => (
                                        <Day key={day}>
                                            <CheckBox
                                                type="checkbox"
                                                name="days"
                                                value={day}
                                                onChange={onChange}
                                                checked={inputLesson.days.includes(day)}
                                            />
                                            {day}
                                        </Day>
                                    ))}
                                </Days>
                            </Field>
                            <Wide>
                                <InputWithLabel label="특이사항" value={inputLesson.etc} onChange={onChange} name="etc" />
                            </Wide>
                        </Fields>
                    </Card>
                </Content>
                <RegisterFooter onSaveButtonClick={onSaveButtonClick} onDeleteButtonClick={id ? onDeleteClickHandler : undefined} />
            </Wrapper>
        </Container>
    );
};

const Container = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    z-index: 10;
    background: ${hw.bg};
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 22px 24px 16px;
    border-radius: 16px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    box-shadow: ${hw.shadow};
    flex: 1;
    min-height: 0;
    overflow: hidden;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    flex-shrink: 0;
`;

const Title = styled.h1`
    margin: 0;
    color: ${hw.text};
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.03em;
`;

const Desc = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: ${hw.textMuted};
`;

const Close = styled.button`
    appearance: none;
    border: 1px solid ${hw.border};
    background: ${hw.surface};
    color: ${hw.textMuted};
    font-size: 13px;
    font-weight: 600;
    border-radius: 8px;
    padding: 8px 12px;
    cursor: pointer;

    &:hover {
        background: ${hw.bg};
        color: ${hw.text};
    }
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    ${scrollbar}
`;

const Card = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    background: ${hw.bg};
    padding: 20px 22px;
    border-radius: 14px;
    border: 1px solid ${hw.border};
`;

const CardTitle = styled.h2`
    margin: 0;
    color: ${hw.text};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.02em;
`;

const Fields = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px 20px;

    @media (max-width: 800px) {
        grid-template-columns: 1fr;
    }
`;

const Field = styled.div<{ $wide?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 8px;
    grid-column: ${props => props.$wide ? "1 / -1" : "auto"};
`;

const Wide = styled.div`
    grid-column: 1 / -1;
`;

const Label = styled.label`
    color: ${hw.text};
    font-size: 14px;
    font-weight: 500;
`;

const Select = styled.select`
    height: 44px;
    padding: 0 12px;
    border-radius: 8px;
    border: 1px solid ${hw.border};
    background: ${hw.surface};
    color: ${hw.text};
    font-size: 14px;

    &:focus {
        outline: none;
        border-color: ${hw.brand};
        box-shadow: 0 0 0 3px rgba(223, 0, 0, 0.12);
    }
`;

const TimeRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const Range = styled.span`
    color: ${hw.textMuted};
    font-weight: 600;
`;

const Days = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px 16px;
`;

const Day = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 600;
    color: ${hw.text};
    cursor: pointer;
`;

const CheckBox = styled.input`
    width: 16px;
    height: 16px;
    margin: 0;
    accent-color: ${hw.brand};
    cursor: pointer;
`;

export default LessonRegister;
